import React, { useEffect, useMemo, useState } from 'react';
import {
  NavLink as RouterNavLink,
  Link as RouterLink,
  useHistory,
  useLocation,
} from 'react-router-dom';
import {
  ArrowLeft,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  FlaskConical,
  LayoutDashboard,
  Paintbrush,
  ScanLine,
  Sparkles,
} from 'lucide-react';

import { useQuery } from '@tanstack/react-query';
import { aiEditorUrl, isAIEditorPath } from '@dashboard/context/AIContext/aiEditorRoute';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { integrationsPageKey, pageCountsKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import getEventsAppUrl from '@dashboard/lib/getEventsAppUrl';
import Logo from '@dashboard/components/Logo';
import { useCommandPalette } from '@dashboard/components/CommandPalette';
import usePrevious from '@dashboard/hooks/usePrevious';
import { useIsLaptop } from '@dashboard/hooks/use-mobile';
import storage from '@dashboard/services/storage';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@dashboard/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarOpenButton,
  SidebarTrigger,
  useSidebar,
} from '@dashboard/components/ui/sidebar';
import {
  Caret,
  NAV_ITEM_CLASS,
  NAV_SUB_ITEM_CLASS,
  NAV_SUB_LIST_CLASS,
  NavBadge,
  SidebarSearchButton,
  TopBarSearchButton,
} from '@dashboard/layouts/NavigationParts';

import { getEditorNavigation, getOverviewScreens, getPageTabs } from '../editorNavigation';
import { DesignNavigation } from '../scenes/PageDesign';
import usePageCommandEntries from './usePageCommandEntries';
import { getAddBookingDisabledReason } from '@dashboard/scenes/PageOverview/scenes/AddBooking/lib/addBookingDisabledReason';
import DarkTooltip from '@dashboard/components/Tooltip';

const backLinkFor = type =>
  type === 'event'
    ? { to: '/events', label: 'Events' }
    : type === 'form'
      ? { to: '/forms', label: 'Forms' }
      : { to: '/checkouts', label: 'Checkout pages' };

const isAtOrUnder = (pathname, url) => pathname === url || pathname.startsWith(`${url}/`);

const OPEN_GROUPS_KEY = 'editor-nav-open-groups';

function readOpenGroups() {
  try {
    return JSON.parse(storage.local.getItem(OPEN_GROUPS_KEY) || '{}');
  } catch {
    return {};
  }
}

// Called from every screen outside a page, so opening one starts with only its current group open.
export function forgetOpenGroups() {
  try {
    storage.local.removeItem(OPEN_GROUPS_KEY);
  } catch {
    // nothing remembered to forget
  }
}

function rememberOpenGroup(label, isOpen) {
  try {
    storage.local.setItem(
      OPEN_GROUPS_KEY,
      JSON.stringify({ ...readOpenGroups(), [label]: isOpen }),
    );
  } catch {
    // storage can be unavailable; the group then just forgets
  }
}

// A collapsible group, open while one of its screens is showing. Opening a screen in another
// group remounts the sidebar, so each group remembers being opened or closed by hand. A group
// open only because its screen was showing is not remembered, so Overview folds away once you
// move on to the editor.
function NavGroupItem({ label, icon: Icon, items, inScope, pathname, onNavigate, meta }) {
  const [isOpen, setOpen] = useState(() => inScope || readOpenGroups()[label] === true);
  const wasInScope = usePrevious(inScope);

  useEffect(() => {
    if (inScope && !wasInScope && !isOpen) setOpen(true);
  }, [inScope, wasInScope, isOpen]);

  const onOpenChange = next => {
    setOpen(next);
    rememberOpenGroup(label, next);
  };

  return (
    <Collapsible asChild open={isOpen} onOpenChange={onOpenChange}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton aria-expanded={isOpen} className={NAV_ITEM_CLASS}>
            <Icon />
            <span>{label}</span>
            {meta ? (
              <span className="ml-auto flex items-center gap-2">
                {meta}
                <Caret isOpen={isOpen} />
              </span>
            ) : (
              <Caret isOpen={isOpen} />
            )}
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className={NAV_SUB_LIST_CLASS}>
            {items.map(item => (
              <SidebarMenuSubItem key={item.url}>
                {item.disabledReason ? (
                  // The tooltip wraps the button: inside it, its Root would swallow the button's styles.
                  <DarkTooltip label={item.disabledReason} side="right" asChild>
                    <SidebarMenuSubButton
                      asChild
                      className={`${NAV_SUB_ITEM_CLASS} cursor-default aria-disabled:pointer-events-auto hover:bg-transparent`}
                    >
                      <span aria-disabled="true">
                        <span className="truncate">{item.label}</span>
                      </span>
                    </SidebarMenuSubButton>
                  </DarkTooltip>
                ) : (
                  <SidebarMenuSubButton
                    asChild
                    isActive={item.isActive(pathname)}
                    className={NAV_SUB_ITEM_CLASS}
                  >
                    {item.external ? (
                      <a href={item.url} target="_blank" rel="noreferrer" onClick={onNavigate}>
                        <span className="truncate">{item.label}</span>
                        <ArrowUpRight
                          className="ml-auto !size-3 shrink-0 !text-sidebar-foreground/60"
                          aria-label="Opens in a new tab"
                        />
                      </a>
                    ) : (
                      <RouterNavLink to={item.url} onClick={onNavigate}>
                        <span className="truncate">{item.label}</span>
                        {item.count > 0 && <NavCount>{item.count.toLocaleString()}</NavCount>}
                      </RouterNavLink>
                    )}
                  </SidebarMenuSubButton>
                )}
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

// Heads the editor's sections in both modes: the AI editor works on the whole page, not one section.
function AIEditorItem({ page, pathname, onNavigate }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isAIEditorPath(pathname)} className={NAV_ITEM_CLASS}>
        <RouterNavLink to={aiEditorUrl(page._id, pathname)} onClick={onNavigate}>
          <Sparkles />
          <span>AI editor</span>
        </RouterNavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function NavCount({ children, ...props }) {
  return (
    <span
      className="ml-auto shrink-0 text-xs tabular-nums text-gray-500 dark:text-muted-foreground"
      {...props}
    >
      {children}
    </span>
  );
}

function usePageCounts(page) {
  const { token, sellerId } = useAuth();
  const { data } = useQuery({
    queryKey: pageCountsKey(page._id),
    queryFn: async () => fetcher(`/sellers/${sellerId}/pages/${page._id}/counts`, token),
    enabled: !!page._id && !!sellerId,
  });
  return data;
}

/** A green dot on Integrations while the page has any integration connected. */
function ActiveIntegrationsDot({ page }) {
  const { token } = useAuth();
  // Same query as the Integrations screen, so opening it reuses this.
  const { data: integrations } = useQuery({
    queryKey: integrationsPageKey(page._id),
    queryFn: async () => fetcher(`/pages/${page._id}/integrations`, token),
    enabled: !!page._id,
  });
  if (!integrations?.length) return null;
  return (
    <span
      role="img"
      aria-label="Active integrations"
      title="Active integrations"
      className="ml-auto size-1.5 shrink-0 rounded-full bg-green-500"
    />
  );
}

function BackLink({ page, onClick }) {
  const backLink = backLinkFor(page.type);
  return (
    <RouterLink
      to={backLink.to}
      onClick={onClick}
      className="flex h-8 w-full min-w-0 items-center gap-2 rounded-md px-2 text-xs font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
    >
      <ArrowLeft className="size-4 shrink-0" />
      <span className="truncate">{backLink.label}</span>
    </RouterLink>
  );
}

/** The top bar's left side: the open button shows here while the sidebar is closed. */
export function EditorTopBarStart() {
  const { isMobile, state } = useSidebar();
  const isLaptop = useIsLaptop();
  if (isMobile) {
    return (
      <div className="flex min-w-0 items-center gap-x-2">
        <SidebarOpenButton className="-ml-2" />
      </div>
    );
  }
  if (isLaptop) return null;
  return (
    <div className="flex shrink-0 items-center gap-2">
      <SidebarTrigger
        className="-ml-2 size-8 text-muted-foreground"
        aria-label={state === 'collapsed' ? 'Open sidebar' : 'Close sidebar'}
      />
    </div>
  );
}

export const EditorSearchButton = TopBarSearchButton;

/** Where the fixed top bar starts: beside the page's sidebar on desktop. */
export function useTopBarStyle() {
  const { isMobile, state } = useSidebar();
  const left =
    state === 'collapsed'
      ? 'var(--sidebar-offset, 0px)'
      : 'calc(var(--sidebar-offset, 0px) + var(--sidebar-width))';
  return {
    left: isMobile ? 0 : left,
    right: 0,
    maxWidth: '2560px',
  };
}

// Where Setup returns to: the last non-design screen of this page, per browser tab.
const setupPathKey = pageId => `editor-setup-path:${pageId}`;

function useEditorMode(page) {
  const { pathname } = useLocation();
  const history = useHistory();
  const base = `/pages/${page._id}`;
  const isDesign = isAtOrUnder(pathname, `${base}/customize/design`);

  const switchMode = mode => {
    if (mode === 'design') {
      history.push(`${base}/customize/design/theme`);
      return;
    }
    history.push(
      storage.session.getItem(setupPathKey(page._id)) ||
        `${base}/customize/${getPageTabs(page)[0].path}`,
    );
  };

  return { isDesign, switchMode };
}

const NAV_BUTTON_CLASS =
  'flex h-8 w-full items-center gap-2 rounded-md px-2 text-[13px] font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent [&>svg]:size-4 [&>svg]:shrink-0';
const OUTLINED_NAV_BUTTON_CLASS = `${NAV_BUTTON_CLASS} border border-sidebar-border`;

// Test payments sits with Theme rather than in the top bar. A page left in test mode says so
// here; turning it off stays inside the panel.
function TestModeNavLink({ page, onNavigate }) {
  const { pathname } = useLocation();
  const to = `/pages/${page._id}/customize/test`;
  const isActive = isAtOrUnder(pathname, to);
  const label = page.type === 'form' ? 'Test submissions' : 'Test payments';

  return (
    <RouterLink
      to={to}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      className={`${NAV_BUTTON_CLASS} ${
        page.testmode === true ? 'border border-chocolate-300 px-[7px] text-chocolate-800' : ''
      } ${isActive ? 'bg-sidebar-accent' : ''}`}
    >
      <FlaskConical />
      <span className="truncate">{page.testmode === true ? 'Test mode on' : label}</span>
      {page.testmode === true && (
        // Same 12px box as the chevrons, so the dot sits on their centre line.
        <span
          aria-hidden="true"
          className="ml-auto flex size-3 shrink-0 items-center justify-center"
        >
          <span className="size-1.5 rounded-full bg-chocolate-600" />
        </span>
      )}
    </RouterLink>
  );
}

export function EditorModeSwitch({ page, className }) {
  const { isDesign, switchMode } = useEditorMode(page);

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => switchMode(isDesign ? 'setup' : 'design')}
        className={isDesign ? OUTLINED_NAV_BUTTON_CLASS : NAV_BUTTON_CLASS}
      >
        {isDesign ? (
          <>
            <ChevronLeft />
            <span>Back</span>
          </>
        ) : (
          <>
            <Paintbrush />
            <span>Theme</span>
            <ChevronRight className="ml-auto !size-3 text-sidebar-foreground/60" />
          </>
        )}
      </button>
    </div>
  );
}

export default function EditorSidebar({ page }) {
  const { seller, isConnectedToStripe } = useAuth();
  const { pathname, state } = useLocation();
  // A record opened from another screen's record (an attendee from a booking) keeps that screen
  // highlighted, across the record's own tabs.
  const [openedFrom, setOpenedFrom] = useState(null);
  const backScreen = state?.back?.screen;
  if (backScreen && (openedFrom?.screen !== backScreen || openedFrom?.record !== pathname))
    setOpenedFrom({ screen: backScreen, record: pathname });
  const { openPalette } = useCommandPalette();
  const { isMobile, setOpenMobile } = useSidebar();

  const base = `/pages/${page._id}`;
  const tabs = useMemo(() => getEditorNavigation({ page, seller }), [page, seller]);
  const screens = useMemo(() => getOverviewScreens(page), [page]);
  const addBookingDisabledReason = getAddBookingDisabledReason({ isConnectedToStripe, page });
  const paletteScreens = useMemo(
    () =>
      addBookingDisabledReason ? screens.filter(screen => screen.path !== 'add-booking') : screens,
    [screens, addBookingDisabledReason],
  );
  usePageCommandEntries({ base, tabs, screens: paletteScreens });
  const counts = usePageCounts(page);

  const onNavigate = () => {
    if (isMobile) setOpenMobile(false);
  };

  const { isDesign } = useEditorMode(page);

  useEffect(() => {
    if (!isDesign && isAtOrUnder(pathname, base))
      storage.session.setItem(setupPathKey(page._id), pathname);
  }, [isDesign, pathname, base, page._id]);

  const checkInItems =
    page.type === 'event'
      ? [
          {
            label: 'Open scanner',
            url: getEventsAppUrl(page._id, 'guest-list'),
            external: true,
            isActive: () => false,
          },
          ...['manage', 'invite'].map(screen => {
            const url = `${base}/staff/${screen}`;
            return {
              label: screen === 'invite' ? 'Invite staff' : 'Manage staff',
              url,
              isActive: path => path === url,
            };
          }),
        ]
      : null;

  const screenPath =
    openedFrom && isAtOrUnder(pathname, openedFrom.record)
      ? `${base}/${openedFrom.screen}`
      : pathname;

  const screenItems = screens.map(screen => {
    const url = `${base}/${screen.path}`;
    return {
      label: screen.label,
      url,
      count: counts?.[screen.path === 'attendees' ? 'guests' : screen.path],
      disabledReason: screen.path === 'add-booking' ? addBookingDisabledReason : null,
      isActive: path => isAtOrUnder(path, url),
    };
  });

  return (
    <Sidebar
      collapsible="offcanvas"
      peekable
      aria-label="Page navigation"
      style={
        isMobile
          ? undefined
          : {
              top: 'var(--top-banner-h, 0px)',
              height: 'calc(100svh - var(--top-banner-h, 0px))',
            }
      }
    >
      {!isMobile && (
        <SidebarHeader className="gap-0 py-0">
          <div className="flex h-14 shrink-0 items-center">
            <BackLink page={page} onClick={onNavigate} />
          </div>
        </SidebarHeader>
      )}

      {isMobile && (
        <SidebarHeader className="gap-0 py-0">
          <div className="flex h-14 items-center pl-2">
            <BackLink page={page} onClick={onNavigate} />
          </div>
          <SidebarMenu className="pb-2">
            <SidebarMenuItem>
              <SidebarSearchButton onClick={() => openPalette('sidebar_click')} />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
      )}

      <SidebarContent className="gap-0">
        {isMobile && <div role="separator" className="mx-4 my-1 h-px shrink-0 bg-sidebar-border" />}

        {isDesign && <EditorModeSwitch page={page} className="px-2 pt-4" />}

        {/* The first item starts level with the rail's icons and the page content. */}
        <SidebarGroup className={isDesign ? undefined : 'pt-4'}>
          <SidebarMenu>
            {isDesign ? (
              <>
                <AIEditorItem page={page} pathname={pathname} onNavigate={onNavigate} />
                <DesignNavigation />
              </>
            ) : (
              [
                <NavGroupItem
                  key="overview"
                  label="Overview"
                  icon={LayoutDashboard}
                  items={screenItems}
                  inScope={screenItems.some(item => item.isActive(screenPath))}
                  pathname={screenPath}
                  onNavigate={onNavigate}
                />,
                <li
                  key="divider"
                  role="separator"
                  className="mx-2 my-1 h-px shrink-0 bg-sidebar-border"
                />,
                ...(checkInItems
                  ? [
                      <NavGroupItem
                        key="check-in"
                        label="Check-in"
                        icon={ScanLine}
                        items={checkInItems}
                        meta={
                          counts?.validTickets ? (
                            <NavCount
                              title={`${counts.checkedIn.toLocaleString()} of ${counts.validTickets.toLocaleString()} guests checked in`}
                            >
                              {counts.checkedIn.toLocaleString()}/
                              {counts.validTickets.toLocaleString()}
                            </NavCount>
                          ) : null
                        }
                        inScope={isAtOrUnder(pathname, `${base}/staff`)}
                        pathname={pathname}
                        onNavigate={onNavigate}
                      />,
                      <li
                        key="check-in-divider"
                        role="separator"
                        className="mx-2 my-1 h-px shrink-0 bg-sidebar-border"
                      />,
                    ]
                  : []),
                <AIEditorItem
                  key="ai-editor"
                  page={page}
                  pathname={pathname}
                  onNavigate={onNavigate}
                />,
              ].concat(
                tabs
                  .filter(tab => tab.path !== 'design')
                  .map(tab => {
                    const tabUrl = `${base}/customize/${tab.path}`;
                    // A tab with one screen is a plain link; a group would only repeat its name.
                    if (tab.flat || tab.sections.length === 1) {
                      const Icon = tab.icon;
                      return (
                        <SidebarMenuItem key={tab.path}>
                          <SidebarMenuButton
                            asChild
                            isActive={isAtOrUnder(pathname, tabUrl)}
                            className={NAV_ITEM_CLASS}
                          >
                            <RouterNavLink
                              to={tab.flat ? tabUrl : `${tabUrl}/${tab.sections[0].path}`}
                              onClick={onNavigate}
                            >
                              <Icon />
                              <span>{tab.label}</span>
                              <NavBadge>{tab.badge}</NavBadge>
                              {tab.path === 'integrations' && <ActiveIntegrationsDot page={page} />}
                            </RouterNavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    }
                    return (
                      <NavGroupItem
                        key={tab.path}
                        label={tab.label}
                        icon={tab.icon}
                        items={tab.sections.map(section => {
                          const url = `${tabUrl}/${section.path}`;
                          return { label: section.label, url, isActive: path => path === url };
                        })}
                        inScope={isAtOrUnder(pathname, tabUrl)}
                        pathname={pathname}
                        onNavigate={onNavigate}
                      />
                    );
                  }),
              )
            )}
          </SidebarMenu>
        </SidebarGroup>

        {!isDesign && (
          <div className="flex flex-col gap-1 px-2 pb-6">
            <div role="separator" className="mx-2 mb-1 h-px shrink-0 bg-sidebar-border" />
            <EditorModeSwitch page={page} />
            <TestModeNavLink page={page} onNavigate={onNavigate} />
          </div>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
